import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../../modules/core/public/ConfirmDialog';
import ConsignmentStatusChip from '../private/ConsignmentStatusChip';
import ConsignmentSettingsGuard from '../private/ConsignmentSettingsGuard';
import { listConsignments, deleteConsignment } from '../private/consignmentApi';

const STATUSES = ['', 'PREPARATION', 'ACTIVE', 'RENEWAL_PENDING', 'RENEWED', 'CLOSED', 'CANCELLED'];
const PAGE_SIZES = [10, 25, 50, 100];

function ConsignmentsPage() {
  const { translate } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async (params) => {
    setLoading(true);
    try {
      const resp = await listConsignments(params);
      setRows(resp?.data?.consignments || []);
      setTotal(resp?.data?.total || 0);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ q, status: statusFilter, sort, order, page, page_size: pageSize });
    }, 300);
  }, [q, statusFilter, sort, order, page, pageSize, load]);

  const handleSort = (field) => {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
    setPage(0);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteConsignment(confirmDelete.id);
      setSnackbar({ open: true, message: translate('consignment.deleted'), severity: 'success' });
      setConfirmDelete(null);
      load({ q, status: statusFilter, sort, order, page, page_size: pageSize });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
  };

  return (
    <ConsignmentSettingsGuard>
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" gap={1} flex={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder={translate('consignment.search_placeholder')}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s ? translate(`consignment.status_${s.toLowerCase()}`) : translate('common.all')}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/consignments/new')}
        >
          {translate('consignment.new_consignment')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sort === 'customer_name'}
                  direction={sort === 'customer_name' ? order : 'asc'}
                  onClick={() => handleSort('customer_name')}
                >
                  {translate('consignment.customer_name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'store_name'}
                  direction={sort === 'store_name' ? order : 'asc'}
                  onClick={() => handleSort('store_name')}
                >
                  {translate('consignment.store_name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{translate('common.status')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'next_visit_date'}
                  direction={sort === 'next_visit_date' ? order : 'asc'}
                  onClick={() => handleSort('next_visit_date')}
                >
                  {translate('consignment.next_visit_date')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'created_at'}
                  direction={sort === 'created_at' ? order : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  {translate('common.created_at')}
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('consignment.no_results')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => (
              <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/consignments/${row.id}`)}>
                <TableCell>
                  <RouterLink
                    to={`/consignments/${row.id}`}
                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.customer_name}
                  </RouterLink>
                </TableCell>
                <TableCell>{row.store_name || '-'}</TableCell>
                <TableCell><ConsignmentStatusChip status={row.status} /></TableCell>
                <TableCell>{formatDate(row.next_visit_date)}</TableCell>
                <TableCell>{formatDate(row.created_at)}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={translate('common.delete')}>
                    <IconButton size="small" onClick={() => setConfirmDelete(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={PAGE_SIZES}
          labelRowsPerPage={translate('common.rows_per_page')}
        />
      </TableContainer>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={translate('consignment.delete_title')}
        message={translate('consignment.delete_message', { name: confirmDelete?.customer_name || '' })}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </ConsignmentSettingsGuard>
  );
}

export default ConsignmentsPage;
